import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Formik, FormikValues } from 'formik';
import * as yup from 'yup';

import AppBar from 'components/AppBar';
import SubmitButton from 'components/Buttons/SubmitButton';
import Field from 'components/Field';
import FormikSubmitErrorEffect from 'components/FormikSubmitErrorEffect';
import Input from 'components/Input';
import Loader from 'components/Loader';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import Section from 'components/Section';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { BillingCompany } from 'models/billingCompany';
import { NationalityCountry } from 'models/country';
import ResponseScreen, { BackgroundTypes } from 'screens/components/ResponseScreen';
import { extractErrorMessage } from 'utils/generic';

import { Container, Form, Fields } from './styles';

interface Props {
  loading: boolean;
  submitting: boolean;
  billingCompany?: BillingCompany;
  countryCurrency: string;
  isFromSelection?: boolean;
  onConfirm: (billingCompany: BillingCompany) => Promise<void>;
  onCloseFormCostCenter?: () => void;
  fetchCountryList: () => Promise<void>;
  nationalityCountries: NationalityCountry[];
  loadingNationalityCountries: boolean;
}

const BillingCompanyForm = ({
  loading,
  submitting,
  billingCompany,
  countryCurrency,
  onConfirm,
  isFromSelection,
  onCloseFormCostCenter,
  fetchCountryList,
  nationalityCountries,
  loadingNationalityCountries,
}: Props) => {
  const { t } = useStoreTranslation();
  const history = useHistory();
  const toast = useToast();
  const [showSuccess, setShowSuccess] = useState(false);

  const isEdit = !!billingCompany && billingCompany.companyId !== '';

  const validationSchema = yup.object().shape({
    legalName: yup.string().trim().required(),
    corporateName: yup.string().trim().required(),
    document: yup.string().required(),
    contactName: yup.string().required(),
    contactLastName: yup.string().required(),
    contactBillingEmail: yup.string().email().required(),
    contactPhoneNumber: yup.string().phone().required(),
    addressStreet: yup.string().required(),
    addressStreetNumber: yup.string().required(),
    addressCityCode: yup.string().required(),
    addressCountryCode: yup.string().required(),
    addressPostalCode: yup.string().required(),
  });

  const initialValues: BillingCompany = {
    companyId: billingCompany?.companyId || undefined,
    legalName: billingCompany?.legalName || '',
    corporateName: billingCompany?.corporateName || '',
    document: billingCompany?.document || '',
    contactName: billingCompany?.contactName || '',
    contactLastName: billingCompany?.contactLastName || '',
    contactBillingEmail: billingCompany?.contactBillingEmail || '',
    addressStreet: billingCompany?.addressStreet || '',
    addressStreetNumber: billingCompany?.addressStreetNumber || '',
    addressCityCode: billingCompany?.addressCityCode || '',
    addressCountryCode: billingCompany?.addressCountryCode || '',
    addressPostalCode: billingCompany?.addressPostalCode || '',
    isActive: billingCompany?.isActive || true,
    contactPhoneNumber: billingCompany?.contactPhoneNumber || '',
  };

  const handleConfirm = async (values: typeof initialValues) => {
    try {
      await onConfirm({
        ...billingCompany,
        ...values,
      } as BillingCompany);
      if (!isFromSelection) setShowSuccess(true);
    } catch (error) {
      if (error.errors) {
        if (error?.errors?.includes('already') !== -1) {
          toast.add({
            type: 'danger',
            message: t('billingCompany.errors.alreadyExists'),
          });
        }
      } else {
        toast.add({
          type: 'danger',
          message: extractErrorMessage(error) || t('defaultError.default'),
        });
      }
    }
  };

  const handleNavigateHistory = () => {
    if (isFromSelection) {
      if (onCloseFormCostCenter) onCloseFormCostCenter();
    } else {
      history.push('/billingCompanyList');
    }
  };

  useEffect(() => {
    if (!nationalityCountries.length && !loadingNationalityCountries) {
      fetchCountryList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCountryList, nationalityCountries]);

  return (
    <Container>
      <Mobile>
        <AppBar goBack={handleNavigateHistory}>
          <h5>{t(!billingCompany?.companyId ? 'billingCompany.form.title' : 'billingCompany.form.title')}</h5>
        </AppBar>
      </Mobile>
      {loading || loadingNationalityCountries ? (
        <Loader fullScreen />
      ) : (
        <>
          <Desktop>
            <ScreenHeader title={t('billingCompany.form.title')} subtitle={t('billingCompany.form.subtitle')} />
          </Desktop>

          <Formik
            isInitialValid={(formik: any) => validationSchema.isValidSync(formik.initialValues)}
            initialValues={initialValues as BillingCompany}
            validationSchema={validationSchema}
            onSubmit={(values: BillingCompany) => handleConfirm(values)}
          >
            {({ dirty, isSubmitting }: FormikValues) => (
              <>
                <FormikSubmitErrorEffect />

                <Form isEdition={isEdit && dirty}>
                  <Fields>
                    <Section title={t('billingCompany.form.company.title')}>
                      <Field name="corporateName" label={t('billingCompany.form.company.corporateNameLabel')}>
                        <Input placeholder={t('billingCompany.form.company.corporateNamePlaceholder')} />
                      </Field>
                      <Field name="legalName" label={t('billingCompany.form.company.legalNameLabel')}>
                        <Input placeholder={t('billingCompany.form.company.legalNamePlaceholder')} />
                      </Field>
                      <Field name="document" label={t('billingCompany.form.company.documentLabel')}>
                        <Input placeholder={t('billingCompany.form.company.documentPlaceholder')} />
                      </Field>
                    </Section>
                    <Section title={t('billingCompany.form.contact.title')}>
                      <Field name="contactName" label={t('billingCompany.form.contact.contactNameLabel')}>
                        <Input placeholder={t('billingCompany.form.contact.contactNamePlaceholder')} />
                      </Field>
                      <Field name="contactLastName" label={t('billingCompany.form.contact.contactLastNameLabel')}>
                        <Input placeholder={t('billingCompany.form.contact.contactLastNamePlaceholder')} />
                      </Field>
                      <Field
                        name="contactBillingEmail"
                        label={t('billingCompany.form.contact.contactBillingEmailLabel')}
                      >
                        <Input placeholder={t('billingCompany.form.contact.contactBillingEmailPlaceholder')} />
                      </Field>
                      <Field name="contactPhoneNumber" label={t('billingCompany.form.contact.contactPhoneNumberLabel')}>
                        <Input placeholder={t('billingCompany.form.contact.contactPhoneNumberPlaceholder')} />
                      </Field>
                    </Section>
                    <Section title={t('billingCompany.form.address.title')}>
                      <Field name="addressStreet" label={t('billingCompany.form.address.addressStreetLabel')}>
                        <Input placeholder={t('billingCompany.form.address.addressStreetPlaceholder')} />
                      </Field>
                      <Field
                        name="addressStreetNumber"
                        label={t('billingCompany.form.address.addressStreetNumberLabel')}
                      >
                        <Input placeholder={t('billingCompany.form.address.addressStreetNumberPlaceholder')} />
                      </Field>
                      <Field name="addressCityCode" label={t('billingCompany.form.address.addressCityCodeLabel')}>
                        <Input placeholder={t('billingCompany.form.address.addressCityCodePlaceholder')} />
                      </Field>
                      <Field name="addressCountryCode" label={t('billingCompany.form.address.addressCountryCodeLabel')}>
                        <Input placeholder={t('billingCompany.form.address.addressCountryCodePlaceholder')} />
                      </Field>
                      <Field name="addressPostalCode" label={t('billingCompany.form.address.addressPostalCodeLabel')}>
                        <Input placeholder={t('billingCompany.form.address.addressPostalCodePlaceholder')} />
                      </Field>
                    </Section>
                  </Fields>
                  <SubmitButton isEdit={isEdit} dirty={dirty} loading={submitting || isSubmitting}>
                    {t(isEdit ? 'billingCompany.buttons.edit' : 'billingCompany.buttons.confirm')}
                  </SubmitButton>
                </Form>
              </>
            )}
          </Formik>

          <ResponseScreen
            show={showSuccess}
            background={BackgroundTypes.Mountain}
            text={t('billingCompany.form.successTitle')}
            subText={
              !!billingCompany?.companyId
                ? t('billingCompany.form.successEditText')
                : t('billingCompany.form.successConfirmText')
            }
            buttonOnClick={() => history.push('/billingCompanyList')}
            buttonText={t('billingCompany.form.successButtonText')}
          />
        </>
      )}
    </Container>
  );
};

export default BillingCompanyForm;
